import './style.css';
import { VectorField } from './engine/VectorField';
import { ParticleSystem } from './engine/ParticleSystem';

// Import shaders using vite-plugin-glsl with explicit suffix for TS
// @ts-ignore
import vertexShaderSource from './shaders/particles.vert?raw';
// @ts-ignore
import fragmentShaderSource from './shaders/particles.frag?raw';

class App {
  private canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram | null = null;
  private particleSystem: ParticleSystem | null = null;
  private vectorField: VectorField;
  
  private lastTime: number = 0;
  private mousePos = { x: 0, y: 0 };
  private isInitialized = false;

  constructor() {
    this.canvas = document.querySelector('#canvas') as HTMLCanvasElement;
    const gl = this.canvas.getContext('webgl2', {
      alpha: false,
      antialias: true,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    });

    if (!gl) {
      throw new Error('WebGL2 not supported');
    }
    this.gl = gl;
    this.vectorField = new VectorField();

    // Bind animate to instance to ensure it's available for init()
    this.animate = this.animate.bind(this);
    
    this.init();
  }

  private createShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error('Could not compile shader: ' + info);
    }
    return shader;
  }

  private createProgram(gl: WebGL2RenderingContext, vertexSrc: string, fragmentSrc: string): WebGLProgram {
    const vs = this.createShader(gl, gl.VERTEX_SHADER, vertexSrc);
    const fs = this.createShader(gl, gl.FRAGMENT_SHADER, fragmentSrc);
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      throw new Error('Could not link program: ' + info);
    }

    // Clean up shaders after linking
    gl.detachShader(program, vs);
    gl.detachShader(program, fs);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    return program;
  }

  private async init() {
    try {
      this.program = this.createProgram(this.gl, vertexShaderSource, fragmentShaderSource);
      this.particleSystem = new ParticleSystem(this.gl, this.program, this.vectorField);

      this.setupEventListeners();
      this.handleResize();
      
      this.isInitialized = true;

      // UI transitions
      const loader = document.getElementById('loader');
      const overlay = document.getElementById('overlay-info');
      if (loader) loader.classList.add('fade-out');
      setTimeout(() => {
        if (overlay) overlay.classList.remove('hidden');
      }, 1000);

      this.lastTime = performance.now();
      requestAnimationFrame(this.animate);
    } catch (err) {
      console.error('Initialization failed:', err);
      const loaderP = document.querySelector('#loader p');
      if (loaderP) loaderP.textContent = 'Hardware Acceleration Required.';
    }
  }

  private setupEventListeners() {
    window.addEventListener('resize', () => this.handleResize());

    // Normalize mouse coords to [-1, 1]
    const updateMouse = (e: MouseEvent | Touch) => {
      this.mousePos.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.mousePos.y = (1 - e.clientY / window.innerHeight) * 2 - 1;
    };

    window.addEventListener('mousemove', (e) => updateMouse(e));
    window.addEventListener('touchstart', (e) => updateMouse(e.touches[0]), { passive: true });
    window.addEventListener('touchmove', (e) => updateMouse(e.touches[0]), { passive: true });
  }

  private handleResize() {
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);

    if (this.particleSystem) {
        this.particleSystem.resize(width, height);
    }
  }

  private animate(now: number) {
    if (!this.isInitialized || !this.program || !this.particleSystem) return;

    const deltaTime = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;

    // Clear canvas
    this.gl.clearColor(0.04, 0.04, 0.04, 1.0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);

    // Update Engine
    this.vectorField.update(deltaTime);
    this.particleSystem.update(deltaTime, this.mousePos);

    // Set Global Uniforms
    this.gl.useProgram(this.program);
    const uTime = this.gl.getUniformLocation(this.program, 'u_time');
    const uRes = this.gl.getUniformLocation(this.program, 'u_resolution');
    const uMouse = this.gl.getUniformLocation(this.program, 'u_mouse');
    const uPointSize = this.gl.getUniformLocation(this.program, 'u_pointSize');

    this.gl.uniform1f(uTime, now * 0.001);
    this.gl.uniform2f(uRes, this.canvas.width, this.canvas.height);
    // Convert normalized [-1, 1] to pixel coords for the shader
    this.gl.uniform2f(uMouse, (this.mousePos.x * 0.5 + 0.5) * this.canvas.width, (0.5 - this.mousePos.y * 0.5) * this.canvas.height);
    
    const baseSize = window.innerWidth < 768 ? 4.0 : 2.5;
    this.gl.uniform1f(uPointSize, baseSize * (window.devicePixelRatio || 1));

    // Render Particles
    this.particleSystem.render();

    requestAnimationFrame(this.animate);
  }
}

// Start app
new App();